import PropTypes from 'prop-types';
import Table from '../../common/Table/Table';
import TableBody from '../../common/Table/TableBody';
import TableCell from '../../common/Table/TableCell';
import TableHeadCell from '../../common/Table/TableHeadCell';
import TableHeader from '../../common/Table/TableHeader';
import TableRow from '../../common/Table/TableRow';
import UploadHistoryPagination from './UploadHistoryPagination';
import localStyles from './styles.module.scss';

const HISTORY_COLUMNS = [
  { title: 'Upload Id', key: 'uploadId', width: '140px' },
  { title: 'File Name', key: 'fileName', width: '180px' },
  { title: 'Error Response', key: 'errorResponse', width: '150px' },
  // { title: 'Stos Created', key: 'stosCreated', width: '180px' },
  { title: 'Uploaded By', key: 'uploadedBy', width: '160px' },
  { title: 'Date & Time', key: 'uploadedAt', width: '130px' },
  { title: 'Status', key: 'status', width: '130px' },
];

const openExternalUrl = (url) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const LinkCell = ({ label, url }) => {
  if (!url) return '-';

  return (
    <button
      type="button"
      className={localStyles.link_button}
      onClick={() => openExternalUrl(url)}>
      {label}
    </button>
  );
};

const STATUS_GREEN = new Set(['COMPLETED', 'SUCCESS', 'COMPLETE', 'SUCCEEDED']);

const STATUS_RED = new Set(['FAILED', 'FAILURE', 'ERROR', 'REJECTED']);

const getStatusPillClass = (rawStatus) => {
  if (!rawStatus) return localStyles.status_pill_neutral;
  const key = String(rawStatus).replaceAll(' ', '_').toUpperCase();
  if (STATUS_GREEN.has(key)) return localStyles.status_pill_green;
  if (STATUS_RED.has(key)) return localStyles.status_pill_red;
  return localStyles.status_pill_yellow;
};

const formatStatusLabel = (rawStatus) => {
  if (!rawStatus) return '';
  return String(rawStatus)
    .replaceAll('_', ' ')
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const StatusPill = ({ status }) => {
  if (!status) return '-';
  const label = formatStatusLabel(status);
  return (
    <span
      className={`${localStyles.status_pill} ${getStatusPillClass(status)}`}>
      {label}
    </span>
  );
};

const getCellContent = (row, key, onUploadIdClick) => {
  switch (key) {
    case 'uploadId':
      if (row.stosCreated?.length) {
        return (
          <button
            type="button"
            className={localStyles.link_button}
            onClick={() => onUploadIdClick(row.stosCreated, row.uploadId, row.uploadedBy)}>
            {row.uploadId || '-'}
          </button>
        );
      }
      return row.uploadId || '-';
    case 'fileName':
      return (
        <LinkCell label={row.fileName || 'View file'} url={row.csvFileUrl} />
      );
    case 'errorResponse':
      return <LinkCell label="View error file" url={row.errorFileUrl} />;
    case 'status':
      return <StatusPill status={row.status} />;
    case 'stosCreated':
      return row.stosCreated?.length ? row.stosCreated.join(', ') : '-';
    default:
      return row[key] || '-';
  }
};

function UploadHistoryTable({
  rows,
  loading,
  error,
  currentPage,
  pageSize,
  totalPages,
  totalElements,
  handlePageChange,
  handlePageSizeChange,
  onUploadIdClick,
}) {
  let content = (
    <div className={localStyles.table_state}>No upload history found.</div>
  );

  if (error) {
    content = <div className={localStyles.table_state_error}>{error}</div>;
  } else if (rows.length > 0 || loading) {
    content = (
      <>
        <div className={localStyles.table_wrapper}>
          <div className={localStyles.table_scroll_area}>
            <Table className={localStyles.history_table}>
              <TableHeader>
                {HISTORY_COLUMNS.map((column) => (
                  <TableHeadCell
                    key={column.key}
                    style={{ width: column.width }}
                    className={localStyles.sticky_head_cell}>
                    {column.title}
                  </TableHeadCell>
                ))}
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.uploadId}>
                    {HISTORY_COLUMNS.map((column) => (
                      <TableCell
                        key={`${row.uploadId}-${column.key}`}
                        style={{ maxWidth: column.width }}>
                        {getCellContent(row, column.key, onUploadIdClick)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading ? (
            <div className={localStyles.table_loading_overlay}>
              <div className={localStyles.table_loading_text}>
                Loading upload history...
              </div>
            </div>
          ) : null}
          <UploadHistoryPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalElements={totalElements}
            currentRowsCount={rows.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </>
    );
  }

  return (
    <div className={localStyles.history_section}>
      <h3 className={localStyles.history_title}>Upload History</h3>
      {content}
    </div>
  );
}

LinkCell.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string,
};

LinkCell.defaultProps = {
  url: '',
};

UploadHistoryTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      uploadId: PropTypes.string,
      fileName: PropTypes.string,
      csvFileUrl: PropTypes.string,
      errorFileUrl: PropTypes.string,
      uploadedBy: PropTypes.string,
      uploadedAt: PropTypes.string,
      status: PropTypes.string,
      stosCreated: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  loading: PropTypes.bool,
  error: PropTypes.string,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalElements: PropTypes.number,
  handlePageChange: PropTypes.func.isRequired,
  handlePageSizeChange: PropTypes.func.isRequired,
  onUploadIdClick: PropTypes.func,
};

UploadHistoryTable.defaultProps = {
  rows: [],
  loading: false,
  error: '',
  currentPage: 0,
  pageSize: 10,
  totalPages: 0,
  totalElements: 0,
  onUploadIdClick: () => {},
};

export default UploadHistoryTable;
